/* eslint-disable @typescript-eslint/no-unsafe-return */
/* eslint-disable no-use-before-define, max-len */
import Axios, {
  AxiosInstance,
  AxiosResponse,
} from 'axios';
import { Client } from 'src/hooks/query/clients/types';
import { JobTitle } from 'src/hooks/query/job-titles/types';
import { UserByRole, UserByRoleRequest } from 'src/hooks/query/usersByRole/types';
import { ConsultancyResponse } from 'src/hooks/query/consultancies/types';
import { Country, GetVendorsRequest, Vendor } from 'src/pages/ViewJob/types';
import { consultancy } from './configuration';

// import {
// } from './configuration';

export type AuthCatalogItem = {
  id: number;
  name?: string;
  value?: string;
  status?: string;
  createdOn?: string;
  updatedOn?: string;
};

/** Normalize panna-auth location/skill payloads for ATS (id + display name as `value`). */
export function normalizeAuthCatalogList(data: unknown): AuthCatalogItem[] {
  let rows: unknown[] = [];
  if (Array.isArray(data)) {
    rows = data;
  } else if (typeof data === 'string') {
    try {
      const parsed = JSON.parse(data);
      rows = Array.isArray(parsed) ? parsed : [];
    } catch {
      rows = [];
    }
  }

  return rows
    .map((raw) => {
      if (!raw || typeof raw !== 'object') {
        return null;
      }
      const item = raw as Record<string, unknown>;
      const id = Number(item.id);
      if (!Number.isFinite(id)) {
        return null;
      }
      const displayName = String(item.name ?? item.value ?? '').trim();
      return {
        id,
        name: displayName,
        value: displayName,
        status: item.status != null ? String(item.status) : undefined,
        createdOn: item.createdOn != null ? String(item.createdOn) : undefined,
        updatedOn: item.updatedOn != null ? String(item.updatedOn) : undefined,
      } as AuthCatalogItem;
    })
    .filter((item): item is AuthCatalogItem => item != null);
}

/**
 * Requests
 */
class Requests {
  static client: Requests;

  axios: AxiosInstance;

  constructor() {
    this.axios = Axios.create({
      baseURL: process.env.REACT_APP_AUTH_SERVICES_API,
    });
    this.axios.defaults.withCredentials = process.env.REACT_APP_AUTH_CROSS_ORIGIN_REQUESTS_ENABLED === 'true';
    this.axios.defaults.timeout = 35000;
  }

  setBearerToken = (token: string) => {
    this.axios.defaults.headers.common.Authorization = `Bearer ${token}`;
  };

  static getInstance = () => {
    if (!Requests.client) {
      Requests.client = new Requests();
    }
    return Requests.client;
  };

  getClients = () => this.axios.get<Client[]>('/clients');

  getJobtitles = () => this.axios.get<JobTitle[]>('/designation');

  getUsersByRoles = (payload: UserByRoleRequest) => this.axios.post<UserByRole[]>('/user/roleInterviewer', payload);

  getConsultancyOptions = (data: GetVendorsRequest) => this.axios.post(consultancy.getConsultancyOptions, data).then(
    (response: AxiosResponse<ConsultancyResponse>) => response.data,
  );

  getConsultancyById = (id: string) => this.axios.get<Vendor>(consultancy.getConsultancyById + id);

  getAllCountries = () => this.axios.get<Country[]>('/country').then((response: AxiosResponse<Country[]>) => response.data);

  getLocations = (q?: string) => this.axios.get<AuthCatalogItem[]>('/locations', {
    params: q?.trim() ? { q: q.trim() } : undefined,
  }).then((response) => ({
    ...response,
    data: normalizeAuthCatalogList(response.data),
  }));

  getSkills = (q?: string) => this.axios.get<AuthCatalogItem[]>('/skills', {
    params: q?.trim() ? { q: q.trim() } : undefined,
  }).then((response) => ({
    ...response,
    data: normalizeAuthCatalogList(response.data),
  }));

  createLocation = (payload: { name: string }) => this.axios.post<AuthCatalogItem>('/locations', payload);

  updateLocation = (id: number, payload: { name: string; status?: string }) => this.axios.put<AuthCatalogItem>(
    `/locations/${id}`,
    payload,
  );

  deleteLocation = (id: number) => this.axios.delete(`/locations/${id}`);

  createSkill = (payload: { name: string }) => this.axios.post<AuthCatalogItem>('/skills', payload);

  updateSkill = (id: number, payload: { name: string; status?: string }) => this.axios.put<AuthCatalogItem>(
    `/skills/${id}`,
    payload,
  );

  deleteSkill = (id: number) => this.axios.delete(`/skills/${id}`);
}

export default Requests;
